import { freeze } from 'icepick';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import zipObject from 'lodash/zipObject';
import { BASE_HIERARCHY_TYPE } from '@atlassian/jira-issue-type-hierarchies';
import { TimeEstimateKey } from '@atlassian/jira-providers-issue';
import type {
	PrefetchedDataGql,
	FeaturesResponse,
	IssueParentGql,
	SprintResponse,
	AvailableEstimation,
	EstimationResponse,
	IssueType,
} from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import {
	CAN_CREATE_ISSUES,
	CAN_MANAGE_SPRINTS,
	CAN_EDIT_ISSUES,
	CAN_EDIT_BOARD_CONFIG,
} from '../../../model/config/permissions/constants';
import {
	BACKLOG,
	BOARD,
	CREATE_ISSUE,
	MANAGE_SPRINT,
	SPRINT,
	ISSUE_LIST,
	NONE,
	EDIT_ISSUE,
	EDIT_BOARD_CONFIG,
	ORIGINAL_ESTIMATE_FIELD_ID,
	ISSUE_COUNT_ID,
	ESTIMATION_TYPE_ID_PREFIX,
	ISSUE_COUNT,
	ORIGINAL_ESTIMATE,
	STORY_POINTS,
} from '../../../model/constants';
import {
	type CardGql,
	type JiraUserGql,
	ENABLED,
	SPRINTS_FEATURE,
	ESTIMATION_FEATURE,
	VERSION_FEATURE,
} from '../../../model/graphql';
import type { IssueParent } from '../../../model/issue-parent/types';
import type {
	Card,
	CardType,
	Person,
	EstimationType,
	BacklogStrategy,
	CardList,
} from '../../../model/types';
import type { SetAppDataPayload } from '../../../state/actions/app';
import type { CardEntityState } from '../../../state/reducers/entities/card/types';
import type { EstimationEntityState } from '../../../state/reducers/entities/estimation/types';
import type { IssueParentsEntityState } from '../../../state/reducers/entities/issue-parent/types';
import type { VersionState } from '../../../state/reducers/entities/version/types';
import {
	nextGenBacklogPageLoad,
	PAGE_LOAD_MARK_TRANSFORM_CRITICAL_START,
	PAGE_LOAD_MARK_TRANSFORM_CRITICAL_END,
	uifBacklogNewUiPageLoad,
} from '../../utils/performance-analytics';

const backlogList = freeze({ id: BACKLOG, type: BACKLOG, name: 'Backlog', issueListKey: null });

const isFeatureEnabled = (features: FeaturesResponse[] | null, featureKey: string): boolean =>
	(features || []).some(
		({ key, status }: FeaturesResponse) => key === featureKey && status === ENABLED,
	);

const isEstimationFeatureEnabled = (features: FeaturesResponse[] | null): boolean =>
	isFeatureEnabled(features, ESTIMATION_FEATURE);

const isVersionsFeatureEnabled = (features: FeaturesResponse[] | null): boolean =>
	isFeatureEnabled(features, VERSION_FEATURE);

export const isSprintsFeatureEnabled = (features: FeaturesResponse[] | null): boolean =>
	isFeatureEnabled(features, SPRINTS_FEATURE);

const isBacklogFeatureEnabled = (features: FeaturesResponse[] | null): boolean =>
	isFeatureEnabled(features, BACKLOG);

const assigneeTransformer = ({ accountId, displayName, avatarUrl }: JiraUserGql): Person => ({
	id: accountId,
	displayName,
	avatarUrl,
});

export const cardTransformer = (
	{
		id,
		issue: { key, type, summary, labels, status, assignee },
		done,
		flagged,
		parentId,
		priority,
		dueDate,
		estimate,
		childIssuesMetadata,
		fixVersions,
	}: CardGql,
	projectId: string | number,
): Card => ({
	summary,
	id: Number(id),
	key,
	labels,
	assignee: assignee ? assigneeTransformer(assignee) : null,
	icon: type.iconUrl,
	isDone: done,
	isFlagged: flagged,
	typeId: Number(type.id),
	issueUrl: `/browse/${key}`,
	statusId: status.id,
	parentId: parentId || '' ? Number(parentId) : null,
	priority: priority || null,
	dueDate: dueDate || '' || null,
	estimate: isNil(estimate) ? null : { ...estimate, timeRemainingEstimate: null },
	numCompleteChildren: childIssuesMetadata?.complete || 0,
	numTotalChildren: childIssuesMetadata?.total || 0,
	fixVersions,
	projectId: Number(projectId),
});

const cardTypesTransformer = (issueTypes: IssueType[]): CardType[] =>
	issueTypes.reduce<Array<CardType>>(
		(baseLevelCardTypes: CardType[], { hierarchyLevelType, id, ...issueType }) => {
			if (hierarchyLevelType === BASE_HIERARCHY_TYPE) {
				baseLevelCardTypes.push({
					id: Number(id),
					typeHierarchyLevel: hierarchyLevelType,
					...issueType,
				});
			}
			return baseLevelCardTypes;
		},
		[],
	);

const storyPointFieldIdTransformer = (
	availableEstimation: AvailableEstimation[],
): string | null => {
	const storyPointId =
		availableEstimation.find(
			({ statisticFieldId }: AvailableEstimation) =>
				statisticFieldId !== ORIGINAL_ESTIMATE_FIELD_ID && statisticFieldId !== ISSUE_COUNT_ID,
		)?.statisticFieldId || '';

	return storyPointId.replace(ESTIMATION_TYPE_ID_PREFIX, '') || null;
};

const getBacklogStrategy = (features: FeaturesResponse[]): BacklogStrategy => {
	if (!isBacklogFeatureEnabled(features)) {
		return NONE;
	}
	if (isSprintsFeatureEnabled(features)) {
		return SPRINT;
	}
	return ISSUE_LIST;
};

export const configTransformer = ({
	jira: { timeTrackingOptions },

	boardScope: {
		board: { rankCustomFieldId },
		projectLocation: { name, features: projectFeatures },
		currentUser: { permissions },
		features,
		estimation,
		backlog: { requestColumnMigration },
	},
}: PrefetchedDataGql) => {
	const transformedConfig = {
		rankCustomFieldId: Number(rankCustomFieldId),
		requestColumnMigration,
		name,
		permissions: {
			[CAN_MANAGE_SPRINTS]: permissions.includes(MANAGE_SPRINT),
			[CAN_CREATE_ISSUES]: permissions.includes(CREATE_ISSUE),
			[CAN_EDIT_ISSUES]: permissions.includes(EDIT_ISSUE),
			[CAN_EDIT_BOARD_CONFIG]: permissions.includes(EDIT_BOARD_CONFIG),
		},
		version: { isVersionsFeatureEnabled: isVersionsFeatureEnabled(projectFeatures) },
		allFeatures: features,
		backlogStrategy: getBacklogStrategy(features),
		storyPointFieldId: storyPointFieldIdTransformer(estimation.available),
		timeTrackingOptions,
	};
	return transformedConfig;
};

export const sprintTransformer = (
	{ id, name, goal, startDate, endDate, daysRemaining, sprintState }: SprintResponse,
	canUpdateSprint: boolean,
) => ({
	id: String(id),
	name,
	type: SPRINT,
	issueListKey: null,
	sprintData: {
		goal: goal || '',
		startDate,
		endDate,
		daysRemaining,
		state: sprintState,
		canUpdateSprint,
	},
});

export const issueParentsTransformer = (issueParents: IssueParentGql[]): IssueParentsEntityState =>
	issueParents.reduce<Record<string, IssueParent>>(
		(issueParentsEntityState: IssueParentsEntityState, issueParent: IssueParentGql) =>
			Object.assign(issueParentsEntityState, {
				[issueParent.id]: issueParent,
			}),
		{},
	);

export const estimationTypeTransformer = (
	isEstimationEnabled: boolean,
	estimation: EstimationResponse,
): EstimationType => {
	const estimateFieldKey = estimation.current.customFieldId || '';

	if (isEstimationEnabled && estimateFieldKey) {
		return estimateFieldKey === TimeEstimateKey ? ORIGINAL_ESTIMATE : STORY_POINTS;
	}

	return ISSUE_COUNT;
};

export const estimationFieldIdTransformer = (
	isEstimationEnabled: boolean,
	estimation: EstimationResponse,
): string | null => (isEstimationEnabled ? estimation.current.customFieldId : null);

export const estimationTransformer = (
	features: FeaturesResponse[],
	estimation: EstimationResponse,
): EstimationEntityState => {
	const isEstimationEnabled = isEstimationFeatureEnabled(features);
	return {
		isEstimationFeatureEnabled: isEstimationEnabled,
		estimationType: estimationTypeTransformer(isEstimationEnabled, estimation),
		estimationFieldId: estimationFieldIdTransformer(isEstimationEnabled, estimation),
		name: '',
	};
};

export const versionTransformer = (allCards: CardEntityState): VersionState => ({
	versions: uniq(
		values(allCards).reduce(
			(fixVersions, card) =>
				card.fixVersions && card.fixVersions.length > 0
					? fixVersions.concat(card.fixVersions)
					: fixVersions,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			[] as string[],
		),
	),
});

export const backlogWithSprintsTransformer = (data: PrefetchedDataGql): SetAppDataPayload => {
	const {
		currentUser: { permissions },
		sprints,
		backlog,
		board: { issueParents },
		features,
		estimation,
		projectLocation: { id: projectId, issueTypes },
	} = data.boardScope;

	const canUpdateSprint = permissions.includes(MANAGE_SPRINT);
	const cardList: Record<string, CardList> = { [BACKLOG]: backlogList };
	const cardListOrder: Array<string> = [];
	const cardOrder: Record<string, number[]> = {
		[BACKLOG]: backlog.cards.reduce<Array<number>>(
			(backlogCardOrder: number[], { id, done }: CardGql) =>
				done ? backlogCardOrder : backlogCardOrder.concat(Number(id)),
			[],
		),
	};

	let cards: Array<CardGql> = [];

	sprints.forEach((sprint: SprintResponse) => {
		cardList[sprint.id] = sprintTransformer(sprint, canUpdateSprint);
		cardListOrder.push(String(sprint.id));
		cardOrder[sprint.id] = sprint.cards.map(({ id }) => Number(id));
		cards = cards.concat(sprint.cards);
	});

	cardListOrder.push(BACKLOG);
	cards = cards.concat(backlog.cards);
	const card = zipObject(
		cards.map(({ id }: CardGql) => id),
		cards.map((cardData) => cardTransformer(cardData, projectId)),
	);

	return {
		entities: {
			cardList,
			cardListOrder,
			card,
			cardTypes: cardTypesTransformer(issueTypes),
			cardOrder,
			issueParents: issueParentsTransformer(issueParents),
			issueParentsOrder: issueParents.map(({ id }: IssueParentGql) => id),
			estimation: estimationTransformer(features, estimation),
			version: versionTransformer(card),
			inlineCreate: { cardTypeIds: issueTypes.map((it) => Number(it.id)) },
		},
		config: configTransformer(data),
	};
};

export const backlogOnlyTransformer = (data: PrefetchedDataGql): SetAppDataPayload => {
	const {
		backlog,
		board,
		board: { issueParents },
		features,
		estimation,
		projectLocation: { id: projectId, issueTypes },
	} = data.boardScope;

	const cards = backlog.cards.concat(board.cards);
	const card = zipObject(
		cards.map(({ id }: CardGql) => id),
		cards.map((cardData) => cardTransformer(cardData, projectId)),
	);

	return {
		entities: {
			cardList: {
				[BOARD]: {
					id: BOARD,
					type: BOARD,
					name: 'Board',
					issueListKey: backlog.boardIssueListKey,
				},
				[BACKLOG]: backlogList,
			},
			cardListOrder: [BOARD, BACKLOG],
			cardTypes: cardTypesTransformer(issueTypes),
			card,
			cardOrder: {
				[BOARD]: board.cards.map(({ id }: CardGql) => Number(id)),
				[BACKLOG]: backlog.cards.map(({ id }: CardGql) => Number(id)),
			},
			issueParents: issueParentsTransformer(issueParents),
			issueParentsOrder: issueParents.map(({ id }: IssueParentGql) => id),
			estimation: estimationTransformer(features, estimation),
			version: versionTransformer(card),
			inlineCreate: { cardTypeIds: issueTypes.map((it) => Number(it.id)) },
		},
		config: configTransformer(data),
	};
};

const transformCriticalData = (
	data: PrefetchedDataGql,
	isCompanyManaged: boolean,
): SetAppDataPayload => {
	const metric = isCompanyManaged ? uifBacklogNewUiPageLoad : nextGenBacklogPageLoad;
	metric.mark(PAGE_LOAD_MARK_TRANSFORM_CRITICAL_START);
	const result = isSprintsFeatureEnabled(data.boardScope.features)
		? backlogWithSprintsTransformer(data)
		: backlogOnlyTransformer(data);
	metric.mark(PAGE_LOAD_MARK_TRANSFORM_CRITICAL_END);
	return result;
};

export default transformCriticalData;
